import {ShoppingCartItem, ShoppingCartValidationError} from '../schema'
import {booking} from '@whyhotel/common-schema'
import {calcOfferPriceSummary} from '@whyhotel/ibe-pricing'
import {formatCurrency} from './utils'
import {getLogger} from './logging'
import {isBeforeDay, formatISODate, toPropertyDateTime} from './dateUtils'

interface LatestOffersAccumulator {
  requests: booking.RatePlanOfferRequest[]
  requestKeys: Set<string>
}

interface SweepEvent {
  date: string
  type: 'start' | 'end'
  index: number // index in itemsWithOffers
}

const logger = getLogger('cartValidation')

/**
 * Checks that shopping cart item's arrival date is not before today.
 *
 * Both the arrival date and "today" are compared in the property's time zone.
 * The arrival string carries the property's UTC offset, but `today` is a JS
 * Date anchored to the user's browser zone — comparing them directly makes the
 * day boundaries disagree for users west of the property, causing a same-day
 * arrival to be wrongly flagged as "before today". Normalizing both to the
 * property zone before the day comparison avoids that.
 * @param shoppingCartItem
 * @param timeZone The property IANA time zone (e.g., "America/New_York")
 * @param today
 * @returns
 */
export function isValidArrivalDate(
  shoppingCartItem: ShoppingCartItem,
  timeZone: string = 'America/New_York',
  today: Date = new Date()
): boolean {
  const arrival = toPropertyDateTime(shoppingCartItem.offer.arrival, timeZone)
  const now = toPropertyDateTime(today, timeZone)
  return !isBeforeDay(arrival, now)
}

/**
 * Validates shopping cart items and removes any items with arrival date before today.
 * @param shoppingCartItems The shopping cart items
 * @param timeZone The property IANA time zone (e.g., "America/New_York")
 * @returns The validated shopping cart items
 */
export function validateShoppingCartArrivalDates(
  shoppingCartItems: ShoppingCartItem[],
  timeZone: string = 'America/New_York'
): ShoppingCartItem[] {
  // remove items from shopping cart if arrival date is before today
  if (shoppingCartItems?.length > 0) {
    const today = new Date()
    return shoppingCartItems.filter(shoppingCartItem => {
      return isValidArrivalDate(shoppingCartItem, timeZone, today)
    })
  } else {
    return []
  }
}

/**
 * Builds a request for the latest offers for shopping cart items.
 * @param shoppingCartItems The shopping cart items
 * @returns
 */
export function buildLatestOffersRequest(
  shoppingCartItems: ShoppingCartItem[],
  timeZone: string
): booking.BulkRatePlanOfferRequest {
  // create unique rate plan offer requests
  const {requests}: LatestOffersAccumulator = shoppingCartItems.reduce(
    (acc: LatestOffersAccumulator, cartItem: ShoppingCartItem) => {
      const ratePlanId = cartItem.offer?.ratePlan.id
      const arrival = formatISODate(
        toPropertyDateTime(cartItem.offer?.arrival, timeZone)
      )
      const departure = formatISODate(
        toPropertyDateTime(cartItem.offer?.departure, timeZone)
      )
      const guests = cartItem.guests
      const requestKey = `${ratePlanId}-${arrival}-${departure}-${guests}`

      if (!acc.requestKeys.has(requestKey)) {
        acc.requestKeys.add(requestKey)
        const request: booking.RatePlanOfferRequest = {
          ratePlanId,
          arrival,
          departure,
          guests
        }
        acc.requests.push(request)
      }

      return acc
    },
    {requests: [], requestKeys: new Set()} as LatestOffersAccumulator
  )

  return {
    requests,
    lastUpdated: new Date().toISOString()
  }
}

/**
 * Finds the latest offer for the shopping cart item.
 * @param cartItem
 * @param latestOffers
 * @returns
 */
function findLatestOffer(
  cartItem: ShoppingCartItem,
  latestOffers: booking.BookingOffer[]
): booking.BookingOffer | null {
  return latestOffers.find(latestOffer => {
    return (
      cartItem.offer.ratePlan.id === latestOffer.ratePlan.id &&
      cartItem.offer.arrival === latestOffer.arrival &&
      cartItem.offer.departure === latestOffer.departure
    )
  })
}

/**
 * Validates that the collective demand for each unit group does not exceed availability.
 * Uses a sweep-line algorithm to find peak concurrent demand across overlapping date ranges.
 * @param shoppingCartItems Items to check (already-flagged items should be excluded)
 * @param latestOffers The latest offers
 * @returns Validation errors for items that exceed unit group availability
 */
export function validateUnitGroupAvailability(
  shoppingCartItems: ShoppingCartItem[],
  latestOffers: booking.BookingOffer[]
): ShoppingCartValidationError[] {
  // Group items by unit group id
  const byUnitGroup = shoppingCartItems.reduce(
    (acc: Record<string, ShoppingCartItem[]>, item) => {
      const unitGroupId = item.offer.unitGroup.id
      if (!acc[unitGroupId]) {
        acc[unitGroupId] = []
      }
      acc[unitGroupId].push(item)
      return acc
    },
    {}
  )

  const errors: ShoppingCartValidationError[] = []

  for (const unitGroupId of Object.keys(byUnitGroup)) {
    const items = byUnitGroup[unitGroupId]
    if (items.length <= 1) {
      continue
    }

    // Resolve latest offers, skip items with no latest offer (handled by per-item check)
    const itemsWithOffers = items
      .map(item => ({item, latestOffer: findLatestOffer(item, latestOffers)}))
      .filter(
        (x): x is {item: ShoppingCartItem; latestOffer: booking.BookingOffer} =>
          x.latestOffer !== null && x.latestOffer !== undefined
      )

    if (itemsWithOffers.length <= 1) {
      continue
    }

    const events: SweepEvent[] = itemsWithOffers.flatMap(({item}, index) => [
      {date: item.offer.arrival, type: 'start', index},
      {date: item.offer.departure, type: 'end', index}
    ])

    // Sort by date; on ties, 'end' before 'start' (back-to-back dates don't overlap)
    events.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date < b.date ? -1 : 1
      }
      if (a.type === b.type) {
        return 0
      }
      return a.type === 'end' ? -1 : 1
    })

    let activeIndices: number[] = []
    let peakItems: number[] = []

    for (const event of events) {
      if (event.type === 'start') {
        activeIndices = [...activeIndices, event.index]
        if (activeIndices.length > peakItems.length) {
          peakItems = [...activeIndices]
        }
      } else {
        activeIndices = activeIndices.filter(i => i !== event.index)
      }
    }

    if (peakItems.length <= 1) {
      continue
    }

    const availableUnits = Math.min(
      ...peakItems.map(i => itemsWithOffers[i].latestOffer.availableUnits)
    )

    if (peakItems.length > availableUnits) {
      // Sort peak items by their original position in shoppingCartItems to preserve earlier-added items
      const sortedPeakItems = [...peakItems].sort((a, b) => {
        const aIdx = shoppingCartItems.indexOf(itemsWithOffers[a].item)
        const bIdx = shoppingCartItems.indexOf(itemsWithOffers[b].item)
        return aIdx - bIdx
      })

      const excessCount = peakItems.length - availableUnits
      const itemsToFlag = sortedPeakItems.slice(-excessCount)

      for (const idx of itemsToFlag) {
        errors.push({
          id: itemsWithOffers[idx].item.id,
          code: 'unit_group_unavailable',
          message:
            'There are not enough rooms available in this room type for the selected dates. ' +
            'This item will be removed from the cart.'
        })
      }
    }
  }

  return errors
}

/**
 * Validates the shopping carts, comparing them to the latest offers
 * @param shoppingCartItems The shopping cart items
 * @param latestOffers The latest offers
 * @param timeZone The property IANA time zone (e.g., "America/New_York")
 * @returns
 */
export function validateShoppingCart(
  shoppingCartItems: ShoppingCartItem[],
  latestOffers: booking.BookingOffer[],
  timeZone: string = 'America/New_York'
): ShoppingCartValidationError[] {
  const cartItemErrors = shoppingCartItems.reduce(
    (acc: ShoppingCartValidationError[], cartItem: ShoppingCartItem) => {
      const ratePlanId = cartItem.offer?.ratePlan.id
      const latestOffer = findLatestOffer(cartItem, latestOffers)

      // get price summaries
      const cartItemPriceSummary = calcOfferPriceSummary(
        cartItem.offer,
        cartItem.additionalServices
      )
      const latestOfferPriceSummary = calcOfferPriceSummary(
        latestOffer,
        cartItem.additionalServices
      )

      if (!isValidArrivalDate(cartItem, timeZone)) {
        // remove item if arrival date is before today
        acc.push({
          id: cartItem.id,
          code: 'invalid_arrival_date',
          message:
            "The room's arrival date is before today.  This item will be removed from the cart."
        })
      } else if (latestOffer.availableUnits <= 0) {
        // no units available
        acc.push({
          id: cartItem.id,
          code: 'not_available',
          message:
            'There are no rooms available for the selected dates.  This item will be removed from the cart.'
        })
      } else if (
        latestOffer.validationMessages &&
        latestOffer.validationMessages.length > 0
      ) {
        // no units available due to restrictions.  ex. rate plan is marked closed, etc.
        logger.warn({
          message: 'Rate plan has validation messages',
          data: {ratePlanId, validationMessages: latestOffer.validationMessages}
        })
        acc.push({
          id: cartItem.id,
          code: 'not_available',
          message:
            'There are no rooms available due to booking restrictions.  This item will be removed from the cart.'
        })
      } else if (latestOfferPriceSummary.total > cartItemPriceSummary.total) {
        // price is higher
        acc.push({
          id: cartItem.id,
          code: 'price_increase',
          message: `The room price has increased from ${formatCurrency(
            cartItemPriceSummary.total
          )} to ${formatCurrency(
            latestOfferPriceSummary.total
          )}.  This item will be updated.`
        })
      } else if (latestOfferPriceSummary.total < cartItemPriceSummary.total) {
        // price is lower
        acc.push({
          id: cartItem.id,
          code: 'price_decrease',
          message: `The room price has decreased from ${formatCurrency(
            cartItemPriceSummary.total
          )} to ${formatCurrency(
            latestOfferPriceSummary.total
          )}.  This item will be updated.`
        })
      }

      return acc
    },
    [] as ShoppingCartValidationError[]
  )

  const invalidCartItemIds = new Set(cartItemErrors.map(e => e.id))
  const validCartItems = shoppingCartItems.filter(
    item => !invalidCartItemIds.has(item.id)
  )
  const availabilityErrors = validateUnitGroupAvailability(
    validCartItems,
    latestOffers
  )

  return [...cartItemErrors, ...availabilityErrors]
}

/**
 * Gets validation errors by shopping cart item ID.
 * @param validationErrors The list of validation errors
 * @returns
 */
export function getValidationErrorsByItem(
  validationErrors: ShoppingCartValidationError[]
): Record<string, ShoppingCartValidationError> {
  // create a lookup map of validation errors
  const validationErrorsByItem = validationErrors.reduce(
    (acc: Record<string, ShoppingCartValidationError>, error) => {
      const id = error.id
      acc[id] = error
      return acc
    },
    {} as Record<string, ShoppingCartValidationError>
  )
  return validationErrorsByItem
}

/**
 * Applies changes to the shopping cart based on validation errors and latest offers.
 * @param shoppingCartItems The shopping cart items
 * @param validationErrors The validation errors
 * @param latestOffers The latest offers
 * @returns
 */
export function applyShoppingCartChanges(
  shoppingCartItems: ShoppingCartItem[],
  validationErrors: ShoppingCartValidationError[],
  latestOffers: booking.BookingOffer[]
): ShoppingCartItem[] {
  // create a lookup map of latest offers
  const validationErrorsByItem = getValidationErrorsByItem(validationErrors)

  const updatedShoppingCartItems = shoppingCartItems.reduce(
    (acc: ShoppingCartItem[], cartItem: ShoppingCartItem) => {
      const cartItemId = cartItem?.id
      const validationError = validationErrorsByItem[cartItemId]
      const latestOffer = findLatestOffer(cartItem, latestOffers)

      if (validationError) {
        switch (validationError.code) {
          case 'invalid_arrival_date':
          case 'not_available':
          case 'price_increase':
          case 'price_decrease':
            if (latestOffer) {
              // update item in cart with latest offer
              logger.info({
                message: 'Updating item in cart',
                data: {validationError, cartItem, latestOffer}
              })

              const updatedCartItem = {
                ...cartItem,
                offer: latestOffer
              }
              acc.push(updatedCartItem)
            } else {
              logger.error({
                message: 'Latest offer not found for item',
                data: {validationError, cartItem, latestOffer}
              })
            }
            break
          case 'unit_group_unavailable':
            logger.info({
              message:
                'Removing item from cart due to unit group unavailability',
              data: {validationError, cartItem}
            })
            // do not push to acc — item is removed
            break
          default:
            logger.error({
              message: 'Unknown validation error code',
              data: {validationError, cartItem}
            })
            break
        }
      } else {
        // add item to cart unchanged if no validation error
        acc.push(cartItem)
      }

      return acc
    },
    [] as ShoppingCartItem[]
  )

  return updatedShoppingCartItems
}
